// ============================================================================
// APP METADATA
// ============================================================================
export const APP_NAME = 'iSDOFSolver';
export const APP_VERSION = '1.0.0';
export const APP_VERSION_POLICY = { currentVersion: APP_VERSION };
export { ORIENTATION_TYPES } from '../shared';

// ============================================================================
// TYPES
// ============================================================================

export type APIPayload = {
  title: string;
  L: number;
  W: number;
  Lo: number;
  orientation: number;
  weight: string;
  KLM: string;
  xsi: number;
  // CSV strings
  Rn: string;
  Xn: string;
  Re: string;
  Xe: string;
  Pa: string;
  td: string;
  d0: number;
  v0: number;
};

export type ChartKey = 'load' | 'displacement' | 'rotation' | 'reaction';

export interface ChartImage {
  mime: 'image/png';
  base64: string; // base64 WITHOUT the data: prefix
}

export interface APIResponse {
  // Scalar Results
  TA: number;
  Me: number;
  Xm: number;
  tmax: number;
  Xmin: number;
  tmin: number;
  rup: number;
  run: number;
  Xen: number;

  // History / Array Data
  ru: number[];
  xe: number[];
  duct: number[];
  rot: number[];
  V1: number;
  V2: number;
  K: number[];
  Tn: number[];

  // Charts (optional + partial means "some keys may be missing")
  charts?: Partial<Record<ChartKey, ChartImage>>;
}

// ============================================================================
// RESPONSE DISPLAY CONFIGURATION
// ============================================================================

import type { SectionConfig } from '../shared';
import { createElement as h } from 'react';

/**
 * Configuration for displaying response data
 * Defines what fields to show and how to format them
 */
export const RESPONSE_DISPLAY_CONFIG: SectionConfig<APIResponse>[] = [
  {
    title: 'Results',
    fields: [
      { key: 'TA', label: 'Tributary Area', getValue: data => h('span', null, data.TA.toFixed(2), 'ft', h('sup', null, '2')) },
      {
        label: 'Stiffness (Inb / Reb) ',
        getValue: data => `[${data.K.map(value => value.toFixed(2)).join(', ')}] kips/in`
      },
      {
        key: 'Me',
        label: 'Effective Mass',
        getValue: data => h('span', null, data.Me.toFixed(3), ' (lb-sec', h('sup', null, '2'), '/in)')
      },
      {
        label: 'Period (Inb / Reb)',
        getValue: data => `[${data.Tn.map(value => value.toFixed(2)).join(', ')}] msec`
      },
      {
        label: 'Ductility (Inb / Reb)',
        getValue: data => `[${data.duct.map(value => value.toFixed(2)).join(', ')}]`
      },
      {
        label: 'Rotation (Inb / Reb)',
        getValue: data => `[${data.rot.map(value => value.toFixed(2)).join(', ')}] deg`
      },
      {
        label: 'Max Displacement',
        getValue: data => `${data.Xm.toFixed(2)} in @ ${data.tmax.toFixed(2)} msec`
      },
      {
        label: 'Min Displacement',
        getValue: data => `${data.Xmin.toFixed(2)} in @ ${data.tmin.toFixed(2)} msec`
      },
      {
        label: 'Reaction (Inb / Reb)',
        getValue: data => `[${(data.V1 / 1000).toFixed(2)}, ${(data.V2 / 1000).toFixed(2)}] kips`
      }
    ]
  }
];

// ============================================================================
// URL PARAMETER MAPPING
// ============================================================================

/**
 * Mapping from URL parameter names to form field names
 * Used for parsing ?inp={...} query params
 */
export const URL_FIELD_MAP: Record<string, string> = {
  title: 'title',
  L: 'length',
  W: 'tributaryWidth',
  Lo: 'hingeDistance',
  orientation: 'orientation',
  weight: 'weight',
  xsi: 'damping',
  Rn: 'resistanceNL',
  Xn: 'deflectionNL',
  Re: 'resistanceLinear',
  Xe: 'deflectionLinear',
  KLM: 'klm',
  P: 'axialLoad',
  Pa: 'pressureHistory',
  td: 'timeHistory',
  d0: 'u0',
  v0: 'v0'
};

/**
 * Value mappings for URL params (e.g., '0' -> 'Vertical')
 * Re-export from shared constants - only includes orientation for this app
 */
export { URL_VALUE_MAPS } from '../shared';
